import { useEffect, useCallback, useMemo } from "react";

import { Route, Routes, useNavigate, useParams } from "react-router-dom";

import { LoadingSkeleton } from "@narmi/design_system";
import { useNotificationContext } from "cerulean";

import CardSelector from "../CardSelector";

import styles from "./SubscriptionManagement.module.scss";
import { useCards } from "../../contexts/CardsContext";
import SubscriptionSummary from "../SubscriptionSummary";
import MerchantDetailsDrawer from "../MerchantDetailsDrawer";

const SubscriptionManagement = () => {
  const { cardUuid } = useParams();
  const { cards, isLoading: isCardsLoading } = useCards();
  const { sendNotification } = useNotificationContext();
  const navigate = useNavigate();

  const cardId = useMemo(() => {
    if (!cardUuid && cards.length > 0) {
      return cards[0].id;
    }

    return cardUuid as API.CardId;
  }, [cardUuid, cards]);

  const handleCardChange = useCallback(
    (newCardId: API.CardId) => {
      navigate(`/cards/${newCardId}/subscriptions`);
    },
    [navigate]
  );

  useEffect(() => {
    if (cards.length === 0 && !isCardsLoading) {
      sendNotification({
        type: "negative",
        text: "Unable to find any cards.",
      });
      navigate("/dashboard", { replace: true });
    }
  }, [cards, isCardsLoading]);

  useEffect(() => {
    if (
      cardId &&
      !cards.find((card) => card.id === cardId) &&
      !isCardsLoading
    ) {
      sendNotification({
        type: "negative",
        text: "Unable to find that card.",
      });
      navigate("/dashboard", { replace: true });
    }
  }, [cards, isCardsLoading]);

  if (isCardsLoading) {
    return <LoadingSkeleton isLoading />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Recurring card transactions</h1>
        <CardSelector
          cardId={cardId}
          cards={cards}
          onChange={handleCardChange}
        />
      </div>
      {cardId && <SubscriptionSummary cardId={cardId} />}
      <Routes>
        <Route
          path=":subscriptionUuid"
          element={<MerchantDetailsDrawer cardId={cardId!} />}
        />
      </Routes>
    </div>
  );
};

export default SubscriptionManagement;
